'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { motion, AnimatePresence } from 'framer-motion';
import { ExperienceType, ExperienceDetailConfig } from '../types';
import axios from 'axios';
import { GridExperienceCard } from '@/components/chat/conversations/experiences/GridExperienceCard';
import { mapExperienceType } from '@/components/chat/conversations/utils/experienceMapper';
import { typography } from '@/design-system/tokens/typography';
import { safeRouterPush } from '@/utils/safeRouterPush';

interface ExperienceRelatedProps {
  experience: unknown;
  type: ExperienceType;
  config: ExperienceDetailConfig;
}

export const ExperienceRelated: React.FC<ExperienceRelatedProps> = ({
  experience,
  type,
  config,
}) => {
  const router = useRouter();
  const [relatedExperiences, setRelatedExperiences] = useState<unknown[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [clickedCardId, setClickedCardId] = useState<string | null>(null);

  useEffect(() => {
    const fetchRelated = async () => {
      if (!experience) return;

      try {
        setIsLoading(true);
        const exp = experience as {
          _id?: string;
          slug?: string;
          guide?: { _id?: string } | string;
        };

        // Handle different API endpoint patterns
        if (typeof config.apiEndpoints.related === 'function') {
          // For stay experiences - GET request with ID
          const relatedId = exp?._id || exp?.slug || '';
          const url = config.apiEndpoints.related(relatedId);
          const response = await axios.get(url);
          setRelatedExperiences(response.data.data?.list || []);
        } else {
          // For activity and package - POST request with payload
          const payload = {
            queryParams: {
              limit: 6,
              offset: 0,
              sort: { price: 'asc', createdAt: 'desc' },
              page: 1,
            },
            filters: {
              excludeSlug: exp?.slug,
              guide:
                typeof exp?.guide === 'object' ? exp.guide?._id : exp?.guide,
            },
          };

          const response = await axios.post(
            config.apiEndpoints.related,
            payload
          );
          setRelatedExperiences(response.data.data?.list || []);
        }
      } catch (err) {
        console.error('Failed to fetch related experiences:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRelated();
  }, [experience, config]);

  // Get guide/host name early so we can show section title even when no related items
  const guideNameForVisibility = (() => {
    if (!experience) return null;
    const exp = experience as {
      guideDetails?: { name?: string };
      guide?: { name?: string } | string;
    };
    if (exp?.guideDetails?.name?.trim()) return exp.guideDetails.name.trim();
    if (exp?.guide && typeof exp.guide === 'object' && exp.guide?.name?.trim())
      return exp.guide.name.trim();
    return null;
  })();

  const hasRelated = relatedExperiences.length > 0;
  const showSection =
    !isLoading && (hasRelated || guideNameForVisibility !== null);
  if (!showSection) return null;

  // Get detail route based on experience type
  const getDetailRoute = (
    relatedExp: unknown,
    expType: ExperienceType
  ): string => {
    const exp = relatedExp as {
      _id?: string;
      slug?: string;
      id?: string;
    };

    const identifier = exp?.slug || exp?.id || exp?._id || '';

    switch (expType) {
      case 'activity':
        return `/activity-details/${identifier}`;
      case 'package':
        return `/package-details/${identifier}`;
      case 'stay':
        // Stay experiences use ID, not slug
        return `/stay-experience-details/${exp?._id || exp?.id || identifier}`;
      default:
        return `/package-details/${identifier}`;
    }
  };

  // Map related experience to GridExperienceCard format
  const mapRelatedExperience = (relatedExp: unknown) => {
    const exp = relatedExp as {
      _id?: string;
      id?: string;
      slug?: string;
      title?: string;
      name?: string;
      shortTitle?: string;
      imageUrl?: string[];
      image?: string;
      place?: string; // Direct location string (like in ChatExperience)
      mainLocation?:
        | {
            name?: string;
            city?: string;
            state?: string;
            country?: string;
          }
        | string; // Main location field (preferred)
      location?:
        | {
            name?: string;
            city?: string;
            state?: string;
            country?: string;
          }
        | string; // Can be object or string
      price?: number;
      currency?: string;
      currencySymbol?: string;
      targetCurrencyRate?: number;
      useGroupPricing?: boolean;
      groupBooking?: Array<{
        isDisplayPrice?: boolean;
        minHeadCount?: number;
        maxHeadCount?: number;
        adultPrice?: number;
        targetCurrencyRate?: number;
      }>;
      pricing?: {
        pricePerPerson?: number;
        currency?: string;
        groupPricing?: Array<{
          isDisplayPrice?: boolean;
          minHeadCount?: number;
          maxHeadCount?: number;
          adultPrice?: number;
        }>;
      };
      description?: string;
      category?: {
        title?: string;
        name?: string;
      };
      type?: string;
      experienceType?: string;
      duration?: string | { days?: number; nights?: number; hours?: number };
      days?: number | { days?: number };
      attractionCount?: number;
      attraction_count?: number;
      attractionsCount?: number;
      highlightsCount?: number;
      attractions?: unknown[];
      highlights?: unknown[];
      activities?: unknown[];
      media?: Array<{
        _id?: string;
        url?: string;
        thumbnailUrl?: string;
        format?: string; // 'image' | 'video'
        category?: string; // 'banner' | 'gallery' etc.
        isActive?: boolean;
        [key: string]: unknown;
      }>;
    };

    // Get price
    let price = 0;
    let currency = exp?.currency || exp?.currencySymbol || 'USD';
    let backendConversionRate = exp?.targetCurrencyRate;

    if (type === 'stay' && exp?.pricing) {
      const displayGroup = exp.pricing.groupPricing?.find(
        g => g.isDisplayPrice
      );
      price = displayGroup
        ? (displayGroup.adultPrice ?? 0) * displayGroup.minHeadCount
        : (exp.pricing.pricePerPerson ?? 0);
      currency = exp.pricing.currency || currency;
    } else {
      const displayGroup = exp?.useGroupPricing
        ? exp?.groupBooking?.find(g => g.isDisplayPrice)
        : undefined;
      price = displayGroup
        ? (displayGroup.adultPrice ?? 0) * displayGroup.minHeadCount
        : (exp?.price ?? 0);
      backendConversionRate =
        typeof displayGroup?.targetCurrencyRate === 'number'
          ? displayGroup.targetCurrencyRate
          : exp?.targetCurrencyRate;
    }

    // Get location string - handle multiple formats (matching experienceMapper pattern)
    const getLocationString = (): string => {
      // First check for direct place field (like ChatExperience)
      if (exp?.place && typeof exp.place === 'string' && exp.place.trim()) {
        return exp.place.trim();
      }

      // Check mainLocation first (preferred API field)
      if (exp?.mainLocation) {
        if (typeof exp.mainLocation === 'string' && exp.mainLocation.trim()) {
          return exp.mainLocation.trim();
        }
        if (typeof exp.mainLocation === 'object') {
          const loc = exp.mainLocation as {
            name?: string;
            city?: string;
            state?: string;
            country?: string;
          };
          // Format: city || name, then add state if available
          const locationStr = `${loc.city || loc.name || ''}${
            loc.state ? `, ${loc.state}` : ''
          }`.trim();
          if (locationStr) return locationStr;
        }
      }

      // Check location field (backward compatibility)
      if (exp?.location) {
        if (typeof exp.location === 'string' && exp.location.trim()) {
          return exp.location.trim();
        }
        if (typeof exp.location === 'object') {
          const loc = exp.location as {
            name?: string;
            city?: string;
            state?: string;
            country?: string;
          };
          // Format: city || name, then add state if available (matching experienceMapper)
          const locationStr = `${loc.city || loc.name || ''}${
            loc.state ? `, ${loc.state}` : ''
          }`.trim();
          if (locationStr) return locationStr;
        }
      }

      return 'Location TBD';
    };

    const locationStr = getLocationString();

    // Get images - handle media array first (for stay experiences), then fallback to imageUrl/image
    const getImages = (): string[] => {
      const images: string[] = [];

      // Check media array first (for stay experiences)
      if (exp?.media && Array.isArray(exp.media)) {
        const mediaImages = exp.media
          .filter((item: unknown) => {
            const mediaItem = item as {
              format?: string;
              isActive?: boolean;
              url?: string;
            };
            return (
              mediaItem.format === 'image' &&
              mediaItem.isActive !== false && // Default to true if not specified
              mediaItem.url &&
              typeof mediaItem.url === 'string' &&
              mediaItem.url.trim().length > 0
            );
          })
          .map((item: unknown) => (item as { url?: string }).url as string)
          .filter(
            (url): url is string => typeof url === 'string' && url.length > 0
          );

        if (mediaImages.length > 0) {
          images.push(...mediaImages);
        }
      }

      // Fallback to imageUrl or image if media array didn't yield results
      if (images.length === 0) {
        if (exp?.imageUrl && Array.isArray(exp.imageUrl)) {
          const validUrls = exp.imageUrl.filter(
            (url): url is string =>
              typeof url === 'string' && url.trim().length > 0
          );
          images.push(...validUrls);
        } else if (exp?.image) {
          if (typeof exp.image === 'string' && exp.image.trim().length > 0) {
            images.push(exp.image);
          } else if (Array.isArray(exp.image)) {
            const validUrls = exp.image.filter(
              (url): url is string =>
                typeof url === 'string' && url.trim().length > 0
            );
            images.push(...validUrls);
          }
        }
      }

      return images;
    };

    const images = getImages();

    // Map type
    const cardType = mapExperienceType(
      exp?.type || exp?.experienceType || type
    );

    // Get attractions count (match ExperienceHeader: support attractionCount from detail API)
    const getAttractionsCount = () => {
      if (typeof exp?.attractionCount === 'number') return exp.attractionCount;
      if (typeof exp?.attraction_count === 'number')
        return exp.attraction_count;
      if (typeof exp?.attractionsCount === 'number')
        return exp.attractionsCount;
      if (typeof exp?.highlightsCount === 'number') return exp.highlightsCount;
      if (Array.isArray(exp?.attractions)) return exp.attractions.length;
      if (Array.isArray(exp?.highlights)) return exp.highlights.length;
      if (Array.isArray(exp?.activities)) return exp.activities.length;
      return 0;
    };

    // Parse duration/days/nights
    let days: number | undefined;
    let durationStr: string | undefined;

    if (exp?.duration) {
      if (typeof exp.duration === 'string') {
        durationStr = exp.duration;
      } else if (typeof exp.duration === 'object') {
        days = exp.duration.days;
      }
    }

    if (days === undefined) {
      if (typeof exp?.days === 'number') days = exp.days;
      else if (typeof exp?.days === 'object') days = exp.days.days;
    }

    // Construct duration object for GridExperienceCard if we have days or nights
    const durationObj = days !== undefined ? { days } : durationStr;

    return {
      id: exp?._id || exp?.id || '',
      title: exp?.shortTitle || exp?.title || exp?.name || 'Experience',
      location: locationStr,
      image: images[0] || '',
      images: images,
      type: cardType,
      price: price > 0 ? String(price) : undefined,
      currency: currency,
      currencySymbol: exp?.currencySymbol,
      backendConversionRate: backendConversionRate,
      description: exp?.description,
      duration: durationObj,
      days: days,
      attractionsCount: getAttractionsCount(),
    };
  };

  const handleCardClick = (
    related: unknown,
    mappedExperience: ReturnType<typeof mapRelatedExperience>
  ) => {
    setClickedCardId(mappedExperience.id);
    const route = getDetailRoute(related, type);
    if (route) {
      // Small delay for click animation
      setTimeout(() => {
        void safeRouterPush(router, route);
      }, 150);
    }
  };

  // Get guide/host name from experience for "Other guided tours by (host name)"
  const guideName = guideNameForVisibility;

  // Get title based on experience type and guide name
  const getTitle = (): string => {
    if (guideName) {
      switch (type) {
        case 'activity':
          return `Other guided tours by ${guideName}`;
        case 'package':
          return `Other packages by ${guideName}`;
        case 'stay':
          return `Other Nexgo Spaces by ${guideName}`;
        default:
          return `Other experiences by ${guideName}`;
      }
    }
    switch (type) {
      case 'package':
        return 'Related Packages';
      case 'activity':
        return 'Related Activities';
      case 'stay':
        return 'Related Nexgo Spaces';
      default:
        return 'Related Experiences';
    }
  };

  return (
    <motion.div
      className='space-y-4'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <motion.h2
        className='font-semibold text-gray-900'
        style={{
          fontSize: typography.size.lg, // 18px
          fontFamily: typography.fonts.primary,
          lineHeight: 'normal',
        }}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
      >
        {getTitle()}
      </motion.h2>
      {hasRelated ? (
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6'>
          <AnimatePresence mode='popLayout'>
            {relatedExperiences.map((related, index) => {
              const mappedExperience = mapRelatedExperience(related);
              const isClicked = clickedCardId === mappedExperience.id;

              return (
                <motion.div
                  key={mappedExperience.id || index}
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{
                    opacity: 1,
                    scale: isClicked ? 0.95 : 1,
                    y: 0,
                  }}
                  exit={{ opacity: 0, scale: 0.9, y: -20 }}
                  whileHover={{
                    scale: 1.02,
                    y: -4,
                    transition: { duration: 0.2 },
                  }}
                  whileTap={{
                    scale: 0.98,
                    transition: { duration: 0.1 },
                  }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.05,
                    ease: 'easeOut',
                  }}
                  className='cursor-pointer'
                >
                  <motion.div
                    animate={
                      isClicked
                        ? {
                            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                          }
                        : {}
                    }
                    transition={{ duration: 0.2 }}
                  >
                    <GridExperienceCard
                      experience={mappedExperience}
                      onClick={() => handleCardClick(related, mappedExperience)}
                    />
                  </motion.div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      ) : (
        <p className='text-gray-500'>
          No other experiences by this host at the moment.
        </p>
      )}
    </motion.div>
  );
};
